import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Cursor-aware currency input directive for nz-input-number.
 *
 * Usage:
 *   <nz-input-number appCurrencyInput formControlName="amount"></nz-input-number>
 *
 * Behaviour:
 *  - On focus     : existing value is fully selected (Backspace clears at once)
 *  - Click        : places logical cursor at the nearest digit position
 *  - Digit key    : if text is selected, clears selection first, then inserts digit
 *  - Backspace    : removes selection, or digit before cursor
 *  - Delete       : removes selection, or digit after cursor
 *  - ArrowLeft    : moves cursor left
 *  - ArrowRight   : moves cursor right
 *  - Paste        : parses pasted text as a decimal amount
 *  - Tab/Enter    : allowed (navigate / submit)
 *  - All other keys are blocked
 *
 * Internal model:
 *  - `digits` is a string of raw digit characters, e.g. "250000" for 2500.00
 *  - The decimal point always sits 2 places from the right
 *  - `cursorPos` is the index within `digits` (0 = before first digit, digits.length = after last)
 */
@Directive({
  selector: 'nz-input-number[appCurrencyInput]',
})
export class CurrencyInputDirective implements AfterViewInit, OnDestroy {
  /** Raw digit string. "250000" represents 2500.00 */
  private digits = '0';

  /** Logical cursor position within `digits` (0 … digits.length). */
  private cursorPos = 1;

  /** True immediately after focus, so the first Backspace/Delete clears all. */
  private isFullySelected = false;

  private inputEl: HTMLInputElement | null = null;

  private readonly focusHandler    = (): void                => this.onFocus();
  private readonly mousedownHandler = (): void               => this.onMouseDown();
  private readonly mouseupHandler  = (): void                => this.onMouseUp();
  private readonly keydownHandler  = (e: KeyboardEvent): void  => this.onKeyDown(e);
  private readonly pasteHandler    = (e: ClipboardEvent): void => this.onPaste(e);

  /** Set to true during the mousedown→mouseup window so we know a click is in progress. */
  private pendingClick = false;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly ngControl: NgControl,
  ) {}

  // ─── lifecycle ───────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.inputEl = this.el.nativeElement.querySelector<HTMLInputElement>('input');
    if (!this.inputEl) return;

    this.inputEl.addEventListener('focus',     this.focusHandler);
    this.inputEl.addEventListener('mousedown', this.mousedownHandler);
    this.inputEl.addEventListener('mouseup',   this.mouseupHandler);
    this.inputEl.addEventListener('keydown',   this.keydownHandler);
    this.inputEl.addEventListener('paste',     this.pasteHandler);
  }

  ngOnDestroy(): void {
    if (!this.inputEl) return;

    this.inputEl.removeEventListener('focus',     this.focusHandler);
    this.inputEl.removeEventListener('mousedown', this.mousedownHandler);
    this.inputEl.removeEventListener('mouseup',   this.mouseupHandler);
    this.inputEl.removeEventListener('keydown',   this.keydownHandler);
    this.inputEl.removeEventListener('paste',     this.pasteHandler);
  }

  // ─── event handlers ──────────────────────────────────────────────────────────

  private onFocus(): void {
    const current = parseFloat(this.ngControl.value) || 0;
    this.digits = this.centsToDigits(Math.round(current * 100));
    this.cursorPos = this.digits.length;
    this.isFullySelected = true;

    setTimeout(() => {
      this.inputEl?.select();
    });
  }

  private onMouseDown(): void {
    // Mark that a click is starting. cursorPos will be resolved on mouseup
    // once the browser has placed the caret, avoiding a race with keydown.
    this.pendingClick = true;
  }

  private onMouseUp(): void {
    if (!this.inputEl || !this.pendingClick) return;
    this.pendingClick = false;

    const start = this.inputEl.selectionStart ?? 0;
    const end   = this.inputEl.selectionEnd   ?? 0;
    const len   = this.inputEl.value.length   ?? 0;

    // Full selection means the user clicked into an already-focused field
    // that still has everything selected from focus — keep isFullySelected.
    if (start === 0 && end === len && len > 0) {
      this.isFullySelected = true;
      this.cursorPos = this.digits.length;
    } else {
      // Use selectionStart for cursor position; if the user drag-selected
      // some text, hasSelection() will catch it in onKeyDown.
      this.cursorPos       = this.visualToLogical(start);
      this.isFullySelected = false;
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    const { key } = e;

    if (/^[0-9]$/.test(key)) {
      e.preventDefault();
      // Delete any highlighted range first, then insert the new digit.
      if (this.hasSelection()) {
        this.deleteSelection();
      } else if (this.isFullySelected) {
        this.clearAll();
      }
      this.isFullySelected = false;
      this.insertDigit(key);
      return;
    }

    switch (key) {
      case 'Backspace':
        e.preventDefault();
        if (this.hasSelection()) {
          this.deleteSelection();
        } else if (this.isFullySelected) {
          this.clearAll();
        } else {
          this.deleteBeforeCursor();
        }
        this.isFullySelected = false;
        break;

      case 'Delete':
        e.preventDefault();
        if (this.hasSelection()) {
          this.deleteSelection();
        } else if (this.isFullySelected) {
          this.clearAll();
        } else {
          this.deleteAfterCursor();
        }
        this.isFullySelected = false;
        break;

      case 'ArrowLeft':
        e.preventDefault();
        this.isFullySelected = false;
        if (this.cursorPos > 0) {
          this.cursorPos--;
        }
        this.restoreVisualCursor();
        break;

      case 'ArrowRight':
        e.preventDefault();
        this.isFullySelected = false;
        if (this.cursorPos < this.digits.length) {
          this.cursorPos++;
        }
        this.restoreVisualCursor();
        break;

      // Allow Tab and Enter so the user can navigate / submit the form.
      case 'Tab':
      case 'Enter':
        this.isFullySelected = false;
        break;

      // Block everything else: letters, symbols, ArrowUp/Down (step), etc.
      default:
        e.preventDefault();
        this.isFullySelected = false;
    }
  }

  private onPaste(e: ClipboardEvent): void {
    e.preventDefault();
    this.isFullySelected = false;

    const text    = e.clipboardData?.getData('text') ?? '';
    const cleaned = text.replace(/[^\d.]/g, '');
    const parsed  = parseFloat(cleaned);

    if (!isNaN(parsed)) {
      const cents     = Math.round(parsed * 100);
      this.digits     = this.centsToDigits(cents);
      this.cursorPos  = this.digits.length;
      this.syncValue();
    }
  }

  // ─── selection helpers ───────────────────────────────────────────────────────

  /**
   * Returns true when the browser currently has a non-collapsed text selection
   * inside the input (i.e. the user has highlighted some characters).
   */
  private hasSelection(): boolean {
    if (!this.inputEl) return false;
    const start = this.inputEl.selectionStart ?? 0;
    const end   = this.inputEl.selectionEnd   ?? 0;
    return end > start;
  }

  /**
   * Delete all digits that fall within the currently highlighted visual range,
   * then collapse the cursor to the start of that range.
   */
  private deleteSelection(): void {
    if (!this.inputEl) return;

    const selStart = this.inputEl.selectionStart ?? 0;
    const selEnd   = this.inputEl.selectionEnd   ?? 0;
    const len      = this.inputEl.value.length   ?? 0;

    // If the entire input is selected, just wipe everything cleanly.
    if (selStart === 0 && selEnd === len && len > 0) {
      this.clearAll();
      return;
    }

    // For the start we use a floor mapping (don't include a digit the caret
    // sits before). For the end we use a ceiling mapping so that if the
    // selection ends right after a separator (comma / dot) we still include
    // the digit that follows it.
    const logicalStart = this.visualToLogical(selStart);
    const logicalEnd   = this.visualToLogicalCeil(selEnd);

    if (logicalStart === logicalEnd) {
      // No actual digits spanned – just move cursor and bail.
      this.cursorPos = logicalStart;
      return;
    }

    this.digits    = this.digits.slice(0, logicalStart) + this.digits.slice(logicalEnd);
    this.cursorPos = logicalStart;
    this.normalize();
    this.syncValue();
  }

  /**
   * Ceiling variant of visualToLogical.
   * If the character AT visualPos (i.e. the one the selection end sits before)
   * is a digit, that digit is considered inside the selection and its index
   * is included in the count. This prevents separators at the right edge of a
   * selection from leaving a residual digit behind.
   */
  private visualToLogicalCeil(visualPos: number): number {
    const formatted = this.formatDigits(this.digits);
    let digitCount  = 0;

    for (let i = 0; i < Math.min(visualPos, formatted.length); i++) {
      if (/\d/.test(formatted[i])) {
        digitCount++;
      }
    }

    // If the selection ends exactly on a separator (not a digit), also pull in
    // any immediately following digit so the whole visual selection is removed.
    if (visualPos < formatted.length && !/\d/.test(formatted[visualPos])) {
      // Peek ahead past the separator to count one more digit if present.
      for (let i = visualPos + 1; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          digitCount++;
          break;
        }
        if (!/[,.]/.test(formatted[i])) {
          break;
        }
      }
    }

    return Math.min(digitCount, this.digits.length);
  }

  // ─── digit manipulation ──────────────────────────────────────────────────────

  private insertDigit(d: string): void {
    const next = this.digits.slice(0, this.cursorPos) + d + this.digits.slice(this.cursorPos);

    this.digits = next;
    this.cursorPos++;
    this.normalize();
    this.syncValue();
  }

  private deleteBeforeCursor(): void {
    if (this.cursorPos === 0) return;

    this.digits    = this.digits.slice(0, this.cursorPos - 1) + this.digits.slice(this.cursorPos);
    this.cursorPos = Math.max(0, this.cursorPos - 1);
    this.normalize();
    this.syncValue();
  }

  private deleteAfterCursor(): void {
    if (this.cursorPos >= this.digits.length) return;

    this.digits = this.digits.slice(0, this.cursorPos) + this.digits.slice(this.cursorPos + 1);
    this.normalize();
    this.syncValue();
  }

  private clearAll(): void {
    this.digits    = '0';
    this.cursorPos = 1;
    this.syncValue();
  }

  /**
   * Remove leading zeros but always keep at least one digit.
   * e.g. "0025" → "25",  "0" → "0"
   */
  private normalize(): void {
    if (this.digits.length === 0) {
      this.digits    = '0';
      this.cursorPos = 1;
      return;
    }

    const stripped = this.digits.replace(/^0+/, '') || '0';
    const removed  = this.digits.length - stripped.length;

    this.digits    = stripped;
    this.cursorPos = Math.max(0, this.cursorPos - removed);
  }

  // ─── cursor mapping ──────────────────────────────────────────────────────────

  /**
   * Convert a visual character offset in the formatted string (e.g. "2,500.00")
   * to a logical index within `this.digits`.
   *
   * We skip over commas and the decimal point.
   */
  private visualToLogical(visualPos: number): number {
    const formatted = this.formatDigits(this.digits);
    let digitCount  = 0;

    for (let i = 0; i < Math.min(visualPos, formatted.length); i++) {
      if (/\d/.test(formatted[i])) {
        digitCount++;
      }
    }

    return Math.min(digitCount, this.digits.length);
  }

  /**
   * After a keyboard move (ArrowLeft/Right) re-position the browser caret
   * to match `this.cursorPos` in the formatted string.
   */
  private restoreVisualCursor(): void {
    if (!this.inputEl) return;

    const formatted  = this.formatDigits(this.digits);
    let   digitsSeen = 0;
    let   visualPos  = formatted.length; // default: end

    for (let i = 0; i < formatted.length; i++) {
      if (digitsSeen === this.cursorPos) {
        visualPos = i;
        break;
      }
      if (/\d/.test(formatted[i])) {
        digitsSeen++;
      }
    }

    // If cursorPos === digits.length, place caret at end
    if (this.cursorPos === this.digits.length) {
      visualPos = formatted.length;
    }

    this.inputEl.setSelectionRange(visualPos, visualPos);
  }

  // ─── formatting & sync ───────────────────────────────────────────────────────

  /**
   * Format a raw digit string into a human-readable currency string.
   * e.g. "250000" → "2,500.00"
   */
  private formatDigits(digits: string): string {
    const cents        = parseInt(digits, 10) || 0;
    const integerPart  = Math.floor(cents / 100);
    const decimalPart  = cents % 100;

    const intStr       = integerPart.toLocaleString('en-US');
    const decStr       = decimalPart.toString().padStart(2, '0');

    return `${intStr}.${decStr}`;
  }

  /**
   * Convert a cents integer to a normalised digit string.
   * e.g. 250000 → "250000"
   */
  private centsToDigits(cents: number): string {
    return cents > 0 ? cents.toString() : '0';
  }

  /**
   * Push the current digits into the Angular form control as a float.
   * After setValue nz-input-number will reformat the visible <input>,
   * then we restore the visual caret so it stays at the logical position.
   */
  private syncValue(): void {
    const cents = parseInt(this.digits, 10) || 0;
    const value = parseFloat((cents / 100).toFixed(2));

    this.ngControl.control?.setValue(value, { emitEvent: true });

    // Restore the caret after nz-input-number rerenders the formatted value.
    setTimeout(() => this.restoreVisualCursor());
  }
}